import React, { useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Typography, 
  Container,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
// import { ThemeToggle } from '@/components/common';
import { useFormik } from 'formik';
import { USER_ROLES } from '@/constants/app';
import { loginValidationSchema } from '@/validation';
import { useAuth, useTheme } from '@/hooks';
import { LoginRequest } from '@/constants/types';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const { login, isAuthenticated, user, isLoading, error, clearAuthError } = useAuth();
  const { mode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      const navigationPath = getNavigationPath();
      navigate(navigationPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearAuthError();
    }
  }, [error, clearAuthError]);

  const getNavigationPath = (): string => {
    if (!user) return '/bookings';
    
    switch (user.role) {
      case USER_ROLES.Booking_SuperAdmin:
      case USER_ROLES.Booking_Admin:
      case USER_ROLES.Booking_Viewer:

        return '/bookings';
      default:
        return '/bookings';
    }
  };

  const formik = useFormik<LoginRequest>({
    initialValues: {
      mobile: '',
      password: '',
      type:'2'
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values) => {
      try {
        await login(values);
        toast.success('Login successful!');
      } catch (error: any) {
        console.error('Login error:', error);
      }
    },
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 3,
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card 
          sx={{ 
            width: '100%', 
            maxWidth: 400,
            boxShadow: 4,
            borderRadius: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <ThemeToggle />
            </Box> */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                component="img"
                src={mode === 'dark' ? '/dark.jpeg' : '/light.jpeg'}
                alt="District Grand Lodge of Madras"
                sx={{
                  maxWidth: 200,
                  height: 'auto',
                  mb: 2,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Sign in to your account
              </Typography>
            </Box>

            <form onSubmit={formik.handleSubmit}>
              <TextField
                fullWidth
                required
                name="mobile"
                label="Email or Mobile Number"
                value={formik.values.mobile}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                helperText={formik.touched.mobile && formik.errors.mobile}
                margin="normal"
                autoComplete="username"
              />

              <TextField
                fullWidth
                required
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                margin="normal"
                autoComplete="current-password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ mt: 3, mb: 2 }}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};